/**
 * Search state type definitions for the unified search surface (VOY-470 epic).
 *
 * These types describe the *internal* shape used by useSearchState and its
 * downstream UI consumers (SmartSearchCard, EditablePillRow, SearchResults).
 *
 * Translation to/from external contracts happens at the boundaries:
 *   - parseURL / parseSessionStorage normalize legacy keys IN
 *     (travelers→passengers, segments→legs, etc.)
 *   - toBookingPayload denormalizes OUT to the keys the existing search-plus
 *     results pipeline already consumes (travelers, segments).
 */

export type Cabin = 'economy' | 'premium-economy' | 'business' | 'first'

export type TripType = 'one-way' | 'round-trip' | 'multi-city'

export type ProductChip = 'flights' | 'hotels' | 'itinerary'

// VOY-461: 'voymytrip' is the third mode — Voy assembles a personalized
// compound trip from memory (the editable inferred-intent preview).
export type SearchMode = 'ai' | 'classic' | 'voymytrip'

export type InputMode = 'chat' | 'web'

export interface AirportRef {
  code: string
  label?: string
}

export interface Passengers {
  adults: number
  children: number
  childrenAges?: number[]
}

export interface Leg {
  from?: AirportRef
  to?: AirportRef
  date?: string
}

export interface ImageSearchAttachment {
  url: string
  description?: string
}

export interface SearchState {
  // AI input
  query: string
  mode: SearchMode
  inputMode: InputMode

  // Parsed / structured fields
  from?: AirportRef
  to?: AirportRef
  departureDate?: string
  returnDate?: string
  passengers: Passengers
  cabin: Cabin
  tripType: TripType

  // Product filter chips (also drive result-tab visibility)
  chips: Record<ProductChip, boolean>

  // Multi-city legs (only when tripType === 'multi-city')
  legs?: Leg[]

  // Provenance / housekeeping
  source?: string
  savedSearchId?: string
  autoSearch: boolean
  imageSearch?: ImageSearchAttachment
}

export const INITIAL_SEARCH_STATE: SearchState = {
  query: '',
  mode: 'ai',
  inputMode: 'chat',
  passengers: { adults: 1, children: 0 },
  cabin: 'economy',
  tripType: 'round-trip',
  chips: { flights: true, hotels: true, itinerary: true },
  autoSearch: false,
}
